import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { ROLE_LABEL } from '../auth/roles'
import { navIcons, logoutIcon } from './NavIcons'
import { navForRole, resolveScreen, type BadgeKey, type NavItem } from '../nav'
import { iniciales, nombreVisible } from '../auth/nombre'

export type Badges = Record<BadgeKey, number>

// La MISMA variable que `nav.ts` y que el pipeline define por ambiente. Hasta
// el 28-set-2026 esto leía `VITE_ENV`, que nadie define, y caía a 'TEST': el
// portal de producción se anunciaba como TEST. Sin build del pipeline
// (`npm run dev`) no hay ambiente que afirmar, y se dice eso.
const AMBIENTE = (import.meta.env.VITE_ENV_NAME as string | undefined)?.toUpperCase() || 'LOCAL'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  badges: Badges
  /** Muestra «Mi bandeja»: lo decide el Layout, que ve el contador. */
  conBandeja: boolean
}

/**
 * Sidebar del portal, 236 px, con la navegación filtrada por rol de Cognito.
 *
 * El asesor ve 3 ítems, el supervisor 3, el admin 6 agrupados en módulos con
 * sub-pestañas (antes eran 18 ítems planos, iguales para todos). La estructura
 * está en `src/nav.ts`; acá solo se dibuja.
 *
 * Un módulo queda activo mientras cualquiera de sus sub-pestañas lo esté: sin
 * eso, entrar a "Usuarios" apagaba "Administración" en el sidebar y el usuario
 * perdía la referencia de dónde estaba.
 */
export default function Sidebar({ collapsed, onToggle, badges, conBandeja }: SidebarProps) {
  const { user, role, logout } = useAuth()
  const { pathname } = useLocation()
  const activo = resolveScreen(role, pathname, conBandeja)

  // El nombre de su cuenta de Google («Ruben Quispe»), no el usuario del
  // email: ver `auth/nombre.ts`.
  const initials = iniciales(user?.name ?? '', user?.email ?? '')
  const displayName = nombreVisible(user?.name ?? '', user?.email ?? '')
  const roleLabel = role ? ROLE_LABEL[role] : 'Sin rol'

  return (
    <aside
      className={`flex h-full flex-col bg-gradient-to-b from-sidebar to-sidebar-deep transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-[236px]'
      }`}
      style={{ padding: '20px 14px', boxSizing: 'border-box' }}
    >
      {/* Cabecera: logo + nombre del portal. Colapsado, el botón va DEBAJO del
          logo: al lado no entra en los 36 px que deja el sidebar de 64, quedaba
          bajo el encabezado de la página y no se podía volver a expandir —con
          el colapso automático de una ventana angosta (DEF-031, 07-oct-2026)
          eso dejaba el menú sin salida—. */}
      <div className={`flex border-b border-sidebar-border pb-5 ${collapsed
        ? 'flex-col items-center gap-2'
        : 'items-center gap-2.5 pl-2 pr-1'}`}>
        <img src="/logo-dark.png" alt="COE" className="h-[30px] w-[30px] shrink-0 object-contain" />
        {!collapsed && (
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="text-[14px] font-bold text-white">Portal PGH</span>
            <span className="text-[11px] text-sidebar-text">COE Automatización IA</span>
          </div>
        )}
        <button
          onClick={onToggle}
          className={`shrink-0 rounded-lg p-1 text-sidebar-text transition-colors hover:bg-sidebar-hover hover:text-white focus:outline-none ${
            collapsed ? '' : 'ml-auto'}`}
          aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {collapsed ? <polyline points="9 18 15 12 9 6" /> : <polyline points="15 18 9 12 15 6" />}
          </svg>
        </button>
      </div>

      {/* Chip de ambiente */}
      {!collapsed && (
        <div className="flex items-center justify-between px-2 pb-[18px] pt-[18px]">
          <span className="text-[10px] font-bold tracking-[0.06em] text-sidebar-text">
            AMBIENTE
          </span>
          <span className="rounded-chip bg-env-bg px-2 py-[3px] text-[10px] font-bold tracking-[0.04em] text-env-text">
            {AMBIENTE}
          </span>
        </div>
      )}

      <nav className="scrollbar-thin flex flex-1 flex-col gap-[18px] overflow-y-auto">
        {navForRole(role, conBandeja).map((group) => (
          <div key={group.label}>
            {!collapsed && (
              <p className="mb-1.5 px-2 text-[10px] font-bold uppercase tracking-[0.08em] text-sidebar-section">
                {group.label}
              </p>
            )}
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <li key={item.label}>
                  <Item
                    item={item}
                    collapsed={collapsed}
                    badges={badges}
                    active={activo.item?.label === item.label}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Pie: usuario y salir. Colapsado, uno debajo del otro: en fila «Salir»
          quedaba fuera de los 64 px, tapado por la página (DEF-031). */}
      <div className={`mt-3.5 flex border-t border-sidebar-border pt-3 ${collapsed
        ? 'flex-col items-center gap-2'
        : 'items-center gap-2.5 px-2'}`}>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-[12px] font-bold text-white">
          {initials}
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-[13px] font-semibold text-white" title={user?.email}>
              {displayName}
            </p>
            <p className="text-[11px] text-sidebar-role">{roleLabel}</p>
          </div>
        )}
        <button
          onClick={logout}
          className="shrink-0 rounded-lg p-1.5 text-sidebar-text transition-colors hover:bg-sidebar-hover hover:text-white focus:outline-none"
          title="Cerrar sesión"
        >
          {collapsed ? logoutIcon : <span className="text-[11px] font-semibold">Salir</span>}
        </button>
      </div>
    </aside>
  )
}

function Item({ item, collapsed, badges, active }: {
  item: NavItem; collapsed: boolean; badges: Badges; active: boolean
}) {
  // El badge del módulo suma los de sus sub-pestañas: el sidebar tiene que
  // avisar que hay algo pendiente sin obligar a abrir el módulo para verlo.
  const propio = item.badge ? badges[item.badge] : 0
  const enTabs = (item.tabs ?? []).reduce(
    (total, tab) => total + (tab.badge ? badges[tab.badge] : 0), 0,
  )
  const count = Math.max(propio, enTabs)

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={`flex items-center rounded-btn transition-colors ${
        collapsed ? 'justify-center px-2 py-2.5' : 'gap-[11px] px-2.5 py-2'
      } ${active
        // El filete verde a la izquierda dice «estás acá» sin depender solo
        // del fondo, que en un sidebar oscuro se distingue poco.
        ? 'bg-sidebar-active text-white shadow-[inset_3px_0_0_0_var(--c-sidebar-mark)]'
        : 'text-sidebar-text hover:bg-sidebar-hover hover:text-white'}`}
      title={collapsed ? item.label : undefined}
    >
      <span className="shrink-0">{navIcons[item.icon]}</span>
      {!collapsed && (
        <>
          <span className="truncate text-chat font-semibold">{item.label}</span>
          {count > 0 && (
            <span
              className="ml-auto shrink-0 animate-badge rounded-chip bg-badge px-1.5 py-px text-[10px] font-bold text-white"
              title={`${count} pendiente${count === 1 ? '' : 's'}`}
            >
              {count}
            </span>
          )}
        </>
      )}
    </NavLink>
  )
}
